import { ChevronRight, Maximize2, Minimize2, Plus } from "lucide-react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { Button } from "@/shared/components/ui/button";
import { NotificationBell } from "@/features/notification-center";
import { ChatButton } from "@/features/chat";
import { OfficeClockButton } from "@/features/office-time/OfficeClockButton";
import { useCreateShiftModal } from "@/features/shift-calendar/createShiftContext";
import { usePermissions } from "@/entities/session/hooks/usePermissions";
import { PERMISSIONS } from "@/shared/config/permissions";
import { useFullscreen } from "@/shared/hooks/useFullscreen";
import { navigationSections } from "@/shared/config/navigation";
import { usePageHeaderMeta } from "./usePageHeader";
import { ThemeToggle } from "./ThemeToggle";

function resolveCrumbLabel(pathname: string): string {
  const match = navigationSections.find(section => {
    if (!section.path) return false;
    return section.path === pathname || pathname.startsWith(`${section.path}/`);
  });
  return match?.label ?? "";
}

export function TopBar() {
  const router = useRouterState();
  const navigate = useNavigate();
  const { openCreateShift } = useCreateShiftModal();
  const { hasPermission } = usePermissions();
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen();

  const crumbLabel = resolveCrumbLabel(router.location.pathname);
  const pageMeta = usePageHeaderMeta();

  const hasPageActions = pageMeta.actions != null;
  const breadcrumbs = pageMeta.breadcrumbs ?? [];

  return (
    <header className="flex h-[52px] shrink-0 items-center justify-between border-b border-line bg-surface px-4">
      <div className="flex min-w-0 items-center">
        {breadcrumbs.length > 0 ? (
          <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5">
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;
              return (
                <div key={`${crumb.label}-${index}`} className="flex min-w-0 items-center gap-1.5">
                  {index > 0 && <ChevronRight size={12} className="shrink-0 text-ink-4" />}
                  {crumb.href && !isLast ? (
                    <Link
                      to={crumb.href}
                      className="truncate text-[13px] text-ink-3 transition-colors hover:text-ink"
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="truncate text-[13px] font-medium text-ink">{crumb.label}</span>
                  )}
                </div>
              );
            })}
          </nav>
        ) : (
          crumbLabel && (
            <h1 className="text-[15px] font-semibold tracking-tight text-ink">{crumbLabel}</h1>
          )
        )}
      </div>

      <div className="flex items-center gap-2">
        {hasPageActions ? (
          <div className="flex items-center gap-2">{pageMeta.actions}</div>
        ) : (
          <>
            {hasPermission(PERMISSIONS.SchedulerShiftsCreate) && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => openCreateShift()}
              >
                <Plus className="h-4 w-4" /> Add shift
              </Button>
            )}
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate({ to: "/incidents" })}
            >
              View incidents
            </Button>
          </>
        )}
        <OfficeClockButton />
        <ChatButton />
        <NotificationBell />
        <ThemeToggle />
        <div className="mx-1 h-5 w-px bg-line" aria-hidden />
        <button
          type="button"
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          className="flex h-9 w-9 items-center justify-center rounded-md text-ink-2 hover:bg-hover focus:outline-none focus:ring-2 focus:ring-[var(--brand-ring)]"
        >
          {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </button>
      </div>
    </header>
  );
}
